import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { FormControl, Input, InputLabel, Tooltip } from '@mui/material'
import { type Field, PuiSelect } from '@pbt/pbt-ui-components'

import type { ReminderProtocol } from '~/api/graphql/generated/types'
import { getPatientName } from '~/store/reducers/patients'

export function VaccineHistoryReminderSelect({
  reminder,
  reminderProtocols,
  reminderProtocolsIsLoading,
  hasDuplicateReminders,
  defaultReminderProtocolId,
  patientId,
}: {
  defaultReminderProtocolId?: string
  hasDuplicateReminders: boolean
  patientId: string | null
  reminder: Field
  reminderProtocols: ReminderProtocol[]
  reminderProtocolsIsLoading: boolean
}) {
  const { t } = useTranslation(['MedicalHistory'])

  useEffect(() => {
    reminder.setValue(defaultReminderProtocolId)
  }, [defaultReminderProtocolId])

  const patientName = useSelector(getPatientName(patientId))

  const SelectComponent = (
    <PuiSelect
      disabled={
        reminderProtocolsIsLoading ||
        reminderProtocols.length === 0 ||
        hasDuplicateReminders
      }
      field={reminder}
      input={<Input id="reminder-select" />}
      isLoading={reminderProtocolsIsLoading}
      items={
        reminderProtocolsIsLoading
          ? []
          : reminderProtocols.map((protocol) => ({
              name: protocol.reminderName,
              id: protocol.id,
            }))
      }
      label={reminder.label}
    />
  )

  const shouldShowTooltip =
    (!reminderProtocolsIsLoading && reminderProtocols.length === 0) ||
    hasDuplicateReminders

  return (
    <FormControl fullWidth>
      <InputLabel htmlFor="reminder-select">{reminder.label}</InputLabel>
      {shouldShowTooltip ? (
        <Tooltip
          placement="top"
          title={
            hasDuplicateReminders
              ? t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.PATIENT_HAS_ACTIVE_REMINDER_VACCINE',
                  { patientName },
                )
              : t(
                  'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.NO_REMINDER_NEEDED',
                )
          }
        >
          {SelectComponent}
        </Tooltip>
      ) : (
        SelectComponent
      )}
    </FormControl>
  )
}
